'use client';

import { crosstabLibraryService } from '@/lib/crosstab/library/list-service';
import { useViewAsListScope } from '@/@subsidiary/hooks/useViewAsListScope';
import type { CrosstabItem } from '@/lib/crosstab/library/types';
import { buildCrosstabContext } from '@/@studio-ai/utils/crosstab-payload';
import { excelToCrosstabData } from '@/@studio-ai/utils/excel-to-crosstab';
import { parseIoohCrosstab } from '@/@studio-ai/utils/iooh-crosstab-parser';
import { Button, Input, Spinner } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useDeferredValue, useRef, useState } from 'react';

type SourceTab = 'mfour' | 'upload' | 'custom';

/**
 * Extracts a search term from a project name hint.
 *
 * Project names follow "YYYY-MM-NNNNN-BrandName Description".
 * The portal API search does not match by project code (e.g. "2025-04-33002"
 * returns 0 results), so we extract the brand/description part that appears
 * after the code — this is what crosstab names actually contain.
 *
 * "2025-04-33002-AT&T Retail Tracker May25" → "AT&T Retail Tracker May25"
 */
function toSearchTerm(hint: string | undefined): string {
    if (!hint) return '';
    // Extract description after "YYYY-MM-NNNNN-"
    const afterCode = hint.match(/^\d{4}-\d{2}-\d+-(.+)/);
    if (afterCode) return afterCode[1];
    return hint;
}

/**
 * Extracts the numeric project ID portion from a project code string.
 * "2025-04-33002" → "2025-04-33002" (kept for badge display only)
 */
function toProjectCode(hint: string | undefined): string {
    if (!hint) return '';
    const codeMatch = hint.match(/^(\d{4}-\d{2}-\d+)/);
    return codeMatch ? codeMatch[1] : '';
}

function relativeTime(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 60) return mins <= 1 ? 'Today' : `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `${weeks}w ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
}

export interface QuestionFrequency {
    qid: string;
    question: string;
    questionType: string;
    frequencies: Array<{ option: string; count: number; pct: number }>;
}

export interface CrosstabDataSummary {
    totalRespondents: number;
    surveyIds: string[];
    questions: QuestionFrequency[];
    /**
     * Pre-serialised crosstab context for files uploaded directly by the user.
     * When set, the planner flow skips the loadData API call and uses this
     * value verbatim as the `crosstab_data` field sent to DANI.
     */
    preBuiltContext?: string;
    /** JSON-stringified ParsedIoohCrosstab — present when the uploaded file is a valid iOOH crosstab. */
    parsedIoohData?: string;
}

interface CrosstabPickerProps {
    isReadOnly?: boolean;
    /** When true, shows a loading spinner on the confirmed state — use while crosstab data is being fetched and sent to DANI. */
    isSubmitting?: boolean;
    onConfirm: (crosstab: CrosstabItem, summary: CrosstabDataSummary) => void;
    initialSelection?: CrosstabItem | null;
    /** Pre-populates the search box and enables the upload tab. Pass the project number or name from the plan context. */
    defaultSearch?: string;
    /**
     * When true (the default), the upload tab stays disabled until a project
     * context (`defaultSearch`) is known. The Reports wizard passes false: it
     * collects a crosstab before any project is chosen, and an upload carries
     * its own project identity.
     */
    uploadRequiresProject?: boolean;
}

export function CrosstabPicker({
    isReadOnly,
    isSubmitting,
    onConfirm,
    initialSelection,
    defaultSearch,
    uploadRequiresProject = true,
}: CrosstabPickerProps) {
    const [activeTab, setActiveTab] = useState<SourceTab>('mfour');

    // ── Upload tab state ───────────────────────────────────────────────────────
    type UploadState = 'idle' | 'parsing' | 'done' | 'error';
    const [uploadState, setUploadState] = useState<UploadState>('idle');
    const [uploadFileName, setUploadFileName] = useState('');
    const [uploadQuestionCount, setUploadQuestionCount] = useState(0);
    const [uploadError, setUploadError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUploadFile = useCallback(
        async (file: File) => {
            setUploadError('');
            setUploadState('parsing');
            setUploadFileName(file.name);
            try {
                // Run both parsers in parallel — iOOH parse fails gracefully for non-iOOH files.
                const [genericResult, ioohResult] = await Promise.allSettled([
                    excelToCrosstabData(file),
                    parseIoohCrosstab(file),
                ]);

                if (genericResult.status === 'rejected') throw genericResult.reason;
                const data = genericResult.value;

                const context = buildCrosstabContext(data);
                setUploadQuestionCount(data.questions.length);
                setUploadState('done');

                const syntheticItem: CrosstabItem = {
                    id: '__upload__',
                    orgId: 'upload',
                    projectId: 0,
                    projectName: file.name,
                    surveyId: 0,
                    name: file.name,
                    status: 'client-created',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                };
                const summary: CrosstabDataSummary = {
                    totalRespondents: data.totalRespondents,
                    surveyIds: data.surveyIds,
                    questions: data.questions.map((q) => ({
                        qid: q.qid,
                        question: q.question,
                        questionType: q.questionType,
                        frequencies: q.options.map((o) => ({
                            option: o.option,
                            count: 0,
                            pct: 0,
                        })),
                    })),
                    preBuiltContext: JSON.stringify(context),
                    parsedIoohData:
                        ioohResult.status === 'fulfilled'
                            ? JSON.stringify(ioohResult.value)
                            : undefined,
                };
                onConfirm(syntheticItem, summary);
            } catch (e) {
                setUploadState('error');
                setUploadError(e instanceof Error ? e.message : 'Failed to parse file');
            }
        },
        [onConfirm],
    );
    // Project code badge label: "2025-04-33002"
    const projectCode = toProjectCode(defaultSearch);
    // Brand name for initial search pre-fill: first token of description after project code
    // "2025-04-33002-AT&T Retail Tracker May25" → "AT&T Retail Tracker May25" → "AT&T"
    const brandName = toSearchTerm(defaultSearch).split(/\s+/)[0] ?? '';

    // Auto-filter terms applied when the user hasn't typed anything:
    // both the project code and the brand name cast a wide net across name/projectName.
    const autoTerms = [projectCode, brandName].filter(Boolean);

    const [search, setSearch] = useState(brandName);
    const [selected, setSelected] = useState<CrosstabItem | null>(initialSelection ?? null);
    const [confirmed, setConfirmed] = useState(!!initialSelection);
    const deferredSearch = useDeferredValue(search);

    // Fetch only the first page sorted by most-recently-updated. The crosstab the user
    // just created for this project will appear at the top. If they need something older
    // they can type in the search box to filter what's shown.
    //
    // A staff member's list is every org's, so while they view as a client it asks for the
    // client's orgs only (M4S-5757), as the classic crosstab list does. Staff drafts (saved
    // with no org) are left out there, as they are from the client's own view.
    const viewAs = useViewAsListScope();
    const {
        data: { items: allItems = [], hasMore: hasMorePages = false } = {},
        isLoading: isListLoading,
        isError,
    } = useQuery({
        queryKey: ['org-crosstabs-recent', 'v1', viewAs.key],
        queryFn: async () => {
            const result = await crosstabLibraryService.getOrgCrosstabs({
                page: 1,
                sortBy: 'updatedAt',
                sortOrder: 'desc',
                ...(viewAs.orgIds ? { subsidiaryOrgIds: [...viewAs.orgIds] } : {}),
            });
            return { items: result.data, hasMore: result.pagination.totalPages > 1 };
        },
        enabled: viewAs.orgReady,
        staleTime: 5 * 60_000,
    });
    const isLoading = isListLoading || !viewAs.orgReady;

    // Include crosstabs scoped to a survey (single-survey/multi-wave) OR a project
    // (multi-survey/multi-wave). Both are valid sources for the DANI data step.
    const withSurvey = allItems.filter(
        (item) =>
            (item.surveyId != null && item.surveyId !== 0) ||
            (item.projectId != null && item.projectId !== 0),
    );

    const matchesTerm = (item: CrosstabItem, term: string) => {
        const t = term.toLowerCase();
        return item.name?.toLowerCase().includes(t) || item.projectName?.toLowerCase().includes(t);
    };

    // When the user has typed, filter by their input.
    // When no user input, auto-filter by project code + brand name (OR logic).
    const items = deferredSearch
        ? withSurvey.filter((item) => matchesTerm(item, deferredSearch))
        : autoTerms.length > 0
          ? withSurvey.filter((item) => autoTerms.some((term) => matchesTerm(item, term)))
          : withSurvey;
    const totalItems = items.length;

    const handleSelect = useCallback(
        (item: CrosstabItem) => {
            if (isReadOnly || confirmed) return;
            // Clicking the already-selected item deselects it
            setSelected((prev) => (prev?.id === item.id ? null : item));
        },
        [isReadOnly, confirmed],
    );

    const handleConfirm = useCallback(() => {
        if (!selected) return;
        setConfirmed(true);
        onConfirm(selected, { totalRespondents: 0, surveyIds: [], questions: [] });
    }, [selected, onConfirm]);

    const handleEdit = useCallback(() => {
        if (isReadOnly) return;
        setConfirmed(false);
    }, [isReadOnly]);

    // Upload is enabled when a project context is known (defaultSearch) so users
    // can upload even when no crosstabs exist for that project yet. Callers that
    // have no project context yet opt out via uploadRequiresProject={false}.
    const uploadEnabled = !uploadRequiresProject || !!defaultSearch;

    const tabs: { key: SourceTab; label: string; icon: string; disabled?: boolean }[] = [
        { key: 'mfour', label: 'Pick from MFour studies', icon: 'lucide:check' },
        {
            key: 'upload',
            label: 'Upload new .xlsx',
            icon: 'lucide:upload',
            disabled: !uploadEnabled,
        },
        { key: 'custom', label: '+ add your own', icon: 'lucide:plus', disabled: true },
    ];

    return (
        <div className="flex flex-col gap-4">
            {/* Source tabs */}
            <div className="flex flex-wrap items-center gap-2">
                {tabs.map((tab) => {
                    const isActive = activeTab === tab.key;
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            disabled={tab.disabled || isReadOnly}
                            onClick={() => !tab.disabled && setActiveTab(tab.key)}
                            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[13px] font-medium transition-colors ${
                                isActive
                                    ? 'bg-foreground text-background'
                                    : tab.disabled
                                      ? 'cursor-not-allowed border border-dashed border-default-300 text-default-400'
                                      : 'border border-default-300 text-default-600 hover:bg-default-100'
                            }`}
                        >
                            {isActive && <Icon icon="lucide:check" width={12} />}
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* MFour picker list */}
            {activeTab === 'mfour' && (
                <div className="flex flex-col gap-3">
                    {confirmed && selected ? (
                        /* ── Selected state ─────────────────────────────── */
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
                                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
                                    <Icon icon="lucide:check" width={16} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-[13px] font-semibold text-foreground">
                                        {selected.projectName
                                            ? `${selected.projectName} · ${selected.name}`
                                            : selected.name}
                                    </p>
                                    <p className="truncate text-[12px] text-blue-600">
                                        {selected.name}
                                    </p>
                                </div>
                                {!isReadOnly && !isSubmitting && (
                                    <Button
                                        size="sm"
                                        variant="bordered"
                                        startContent={<Icon icon="lucide:pencil" width={12} />}
                                        onPress={handleEdit}
                                        className="flex-shrink-0 rounded-full border-default-300 text-[12px] text-default-600"
                                    >
                                        Edit
                                    </Button>
                                )}
                            </div>
                            {isSubmitting && (
                                <div className="flex items-center gap-2 px-1">
                                    <Spinner size="sm" color="primary" />
                                    <span className="text-[12px] text-default-500">
                                        Loading crosstab data…
                                    </span>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* ── Selection list ─────────────────────────────── */
                        <>
                            {projectCode ? (
                                <div className="flex items-center gap-2">
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-mfour-green/10 px-2.5 py-1 text-[12px] font-medium text-mfour-green">
                                        <Icon icon="lucide:filter" width={11} />
                                        Project: {projectCode}
                                    </span>
                                    <span className="text-[12px] text-default-400">
                                        crosstabs only
                                    </span>
                                </div>
                            ) : (
                                <p className="text-[13px] leading-relaxed text-foreground">
                                    Here are the crosstabs from your workspace. Pick one:
                                </p>
                            )}

                            <div className="flex flex-col overflow-hidden rounded-xl border border-default-200 bg-background">
                                {/* Search */}
                                <div className="border-b border-default-100 p-2">
                                    <Input
                                        placeholder={
                                            projectCode
                                                ? `Search within project ${projectCode}...`
                                                : `Search ${totalItems > 0 ? totalItems : ''} crosstabs by brand, study, or file name...`
                                        }
                                        value={search}
                                        onValueChange={setSearch}
                                        size="sm"
                                        startContent={
                                            <Icon
                                                icon="lucide:search"
                                                width={14}
                                                className="text-default-400"
                                            />
                                        }
                                        classNames={{
                                            inputWrapper: 'bg-transparent shadow-none border-none',
                                            input: 'text-[13px]',
                                        }}
                                    />
                                </div>

                                {/* Count + overflow hint */}
                                {!isLoading && totalItems > 0 && (
                                    <p className="border-b border-default-100 px-3 py-1.5 text-[11px] text-default-400">
                                        {totalItems} crosstab{totalItems !== 1 ? 's' : ''}
                                        {hasMorePages && !deferredSearch && (
                                            <span className="ml-1 text-default-300">
                                                · search to find others
                                            </span>
                                        )}
                                    </p>
                                )}

                                {/* List */}
                                <div className="max-h-[280px] overflow-y-auto">
                                    {isLoading && (
                                        <div className="flex items-center justify-center py-8">
                                            <Spinner size="sm" />
                                        </div>
                                    )}
                                    {isError && (
                                        <p className="px-4 py-6 text-center text-[13px] text-danger">
                                            Failed to load crosstabs. Please try again.
                                        </p>
                                    )}
                                    {!isLoading && !isError && items.length === 0 && (
                                        <div className="flex flex-col items-center gap-3 px-4 py-6 text-center">
                                            <p className="text-[13px] text-default-500">
                                                {search
                                                    ? 'No results found.'
                                                    : projectCode
                                                      ? 'No crosstabs with survey data found for this project.'
                                                      : 'No crosstabs found in your workspace.'}
                                            </p>
                                            {uploadEnabled && items.length === 0 && !isLoading && (
                                                <button
                                                    type="button"
                                                    onClick={() => setActiveTab('upload')}
                                                    className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-default-300 px-3 py-1 text-[12px] text-default-500 transition-colors hover:border-default-400 hover:text-default-700"
                                                >
                                                    <Icon icon="lucide:upload" width={12} />
                                                    Upload a crosstab for this project
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {items.map((item, idx) => {
                                        const isSelected = selected?.id === item.id;
                                        return (
                                            <button
                                                key={item.id}
                                                type="button"
                                                onClick={() => handleSelect(item)}
                                                className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                                                    idx !== items.length - 1
                                                        ? 'border-b border-default-100'
                                                        : ''
                                                } ${
                                                    isSelected
                                                        ? 'bg-primary/5'
                                                        : 'hover:bg-default-50'
                                                }`}
                                            >
                                                {/* Selection indicator */}
                                                <div
                                                    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border transition-colors ${
                                                        isSelected
                                                            ? 'border-mfour-green bg-mfour-green'
                                                            : 'border-default-300'
                                                    }`}
                                                >
                                                    {isSelected && (
                                                        <Icon
                                                            icon="lucide:check"
                                                            width={10}
                                                            className="text-white"
                                                        />
                                                    )}
                                                </div>

                                                {/* Details */}
                                                <div className="min-w-0 flex-1">
                                                    <p className="truncate text-[13px] font-medium text-foreground">
                                                        {item.projectName
                                                            ? `${item.projectName} · ${item.name}`
                                                            : item.name}
                                                    </p>
                                                    <p className="truncate text-[11px] text-default-400">
                                                        {item.name}
                                                    </p>
                                                </div>

                                                {/* Date */}
                                                <span className="flex-shrink-0 text-[11px] text-default-400">
                                                    {relativeTime(item.updatedAt)}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {selected && (
                                <Button
                                    className="w-fit bg-mfour-green font-medium text-white data-[hover=true]:bg-mfour-green/90"
                                    size="sm"
                                    onPress={handleConfirm}
                                    endContent={<Icon icon="lucide:arrow-right" width={14} />}
                                >
                                    Continue
                                </Button>
                            )}
                        </>
                    )}
                </div>
            )}

            {activeTab === 'upload' && (
                <div className="flex flex-col gap-3">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        className="hidden"
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleUploadFile(file);
                            e.target.value = '';
                        }}
                    />

                    {/* Idle / drop zone */}
                    {uploadState === 'idle' && (
                        <button
                            type="button"
                            disabled={isReadOnly}
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault();
                                const file = e.dataTransfer.files[0];
                                if (file) handleUploadFile(file);
                            }}
                            className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-default-200 bg-default-50 px-6 py-8 text-center transition-colors hover:border-primary/40 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-default-100">
                                <Icon
                                    icon="lucide:file-spreadsheet"
                                    width={20}
                                    className="text-default-500"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <p className="text-[13px] font-medium text-foreground">
                                    Upload a crosstab file
                                </p>
                                <p className="text-[12px] text-default-400">
                                    Drag &amp; drop or click to browse — accepts .xlsx
                                </p>
                            </div>
                        </button>
                    )}

                    {/* Parsing */}
                    {uploadState === 'parsing' && (
                        <div className="flex items-center gap-3 rounded-xl border border-default-200 bg-default-50 px-5 py-4">
                            <Spinner size="sm" color="primary" />
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-[13px] font-medium text-foreground">
                                    {uploadFileName}
                                </p>
                                <p className="text-[12px] text-default-400">Reading questions…</p>
                            </div>
                        </div>
                    )}

                    {/* Done */}
                    {uploadState === 'done' && (
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
                                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
                                    <Icon icon="lucide:check" width={16} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-[13px] font-semibold text-foreground">
                                        {uploadFileName}
                                    </p>
                                    <p className="text-[12px] text-blue-600">
                                        {uploadQuestionCount} question
                                        {uploadQuestionCount !== 1 ? 's' : ''} extracted
                                    </p>
                                </div>
                                {!isReadOnly && (
                                    <Button
                                        size="sm"
                                        variant="bordered"
                                        startContent={<Icon icon="lucide:upload" width={12} />}
                                        onPress={() => {
                                            setUploadState('idle');
                                            setUploadFileName('');
                                        }}
                                        className="flex-shrink-0 rounded-full border-default-300 text-[12px] text-default-600"
                                    >
                                        Replace
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Error */}
                    {uploadState === 'error' && (
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-3 rounded-xl border border-danger/20 bg-danger/5 px-4 py-3">
                                <Icon
                                    icon="lucide:alert-circle"
                                    width={18}
                                    className="flex-shrink-0 text-danger"
                                />
                                <div className="min-w-0 flex-1">
                                    <p className="text-[13px] font-medium text-foreground">
                                        Could not read file
                                    </p>
                                    <p className="text-[12px] text-default-500">{uploadError}</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setUploadState('idle');
                                    setUploadError('');
                                }}
                                className="self-start text-[12px] text-primary hover:underline"
                            >
                                Try another file
                            </button>
                        </div>
                    )}
                </div>
            )}
            {activeTab === 'custom' && (
                <p className="text-[13px] text-default-400">Custom source coming soon.</p>
            )}
        </div>
    );
}
